import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { takePostLoginPath } from "@/lib/redirect";
import { marketingUrl } from "@/lib/appLink";
import { Layout } from "@/components/Layout";
import { DesktopOnly } from "@/components/DesktopOnly";
import { TermsGate } from "@/components/TermsGate";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Sites from "@/pages/Sites";
import SiteDetail from "@/pages/SiteDetail";
import Audits from "@/pages/Audits";
import ExtensionAuthorize from "@/pages/ExtensionAuthorize";
import Settings from "@/pages/Settings";
import Feedback from "@/pages/Feedback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

/* Leaves the SPA for a page hosted elsewhere. Used for routes that moved to the
   public marketing site, so old bookmarks and in-app links still land. */
const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <p className="font-mono text-sm text-ink-muted" role="status">
        Opening the help center. <a className="underline text-[#16324F]" href={to}>Continue</a>
      </p>
    </div>
  );
};

const Protected = ({ children }) => {
  const { session, loading, termsRequired } = useAuth();
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  // Supabase drops a redirect path that is not on its allowlist and returns to
  // the site root instead, so the destination is also parked in session storage
  // and claimed on the first protected render after sign-in. Read through a ref
  // so that reading it, which also clears it, happens once per mount and the
  // redirect is decided before anything else paints.
  const pending = useRef(null);
  if (pending.current === null && !loading && session) pending.current = takePostLoginPath();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <p className="font-mono text-sm text-ink-muted" role="status">Loading session...</p>
      </div>
    );
  }
  if (!session) {
    // The extension sends people to /extension/authorize directly. Losing that
    // destination at the login wall would abandon the pairing they started.
    const next = here === "/" ? "" : `?next=${encodeURIComponent(here)}`;
    return <Navigate to={`/login${next}`} replace />;
  }
  /* Ahead of the pending redirect and of Layout, so consent is settled before
     anything else, including the extension pairing screen. This component stays
     mounted while the gate is up, so the destination held in the ref survives
     and is applied on the render after acceptance. Every route behind the login
     passes through here, which is what keeps the check in one place. */
  if (termsRequired) return <TermsGate />;
  if (pending.current && pending.current !== here) return <Navigate to={pending.current} replace />;
  /* Released the moment the destination is reached, and this is not a detail of
     bookkeeping. Every route renders this component at the same position in the
     tree and React Router puts no key on it, so moving from one page to the next
     does not remount it: a ref claimed once outlives the navigation it was
     claimed for. Left set, the check above fired again on the next click and
     sent the user straight back, which is what a page that flickers and refuses
     to change actually is. Cleared here rather than above the terms gate so the
     destination still survives the acceptance screen. */
  pending.current = "";

  return <Layout>{children}</Layout>;
};

function App() {
  return (
    // Outside the auth provider, not around individual routes: a phone should
    // not be walked through the login form only to be stopped at the first
    // page, and should not open a session it will never use.
    <DesktopOnly>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/sites" element={<Protected><Sites /></Protected>} />
            <Route path="/sites/:siteId" element={<Protected><SiteDetail /></Protected>} />
            <Route path="/audits" element={<Protected><Audits /></Protected>} />
            <Route path="/audits/unassigned" element={<Navigate to="/audits" replace />} />
            <Route path="/extension/authorize" element={<Protected><ExtensionAuthorize /></Protected>} />
            <Route path="/connect" element={<Navigate to="/extension/authorize" replace />} />
            <Route path="/billing" element={<Navigate to="/settings?tab=billing" replace />} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            {/* The help centre is public and lives on the marketing site, so the
                old in-app route sends people there rather than keeping a second
                copy of the documentation behind the login. */}
            <Route path="/help" element={<ExternalRedirect to={marketingUrl("/help")} />} />
            <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{ style: { borderRadius: 2, border: "1px solid #D1D5DB", boxShadow: "none", fontFamily: "Public Sans" } }}
        />
      </AuthProvider>
    </DesktopOnly>
  );
}

export default App;
